import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  BookOpen, 
  Zap, 
  Trophy,
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthenticated, 
    loading: authLoading,
    authInProgress,
    processSession, 
    loginWithEmail, 
    registerWithEmail,
    checkAuth 
  } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingOAuth, setProcessingOAuth] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  // Check for OAuth callback - check both query params and hash
  useEffect(() => {
    const processOAuthCallback = async () => {
      // Check query params first
      const params = new URLSearchParams(location.search);
      let sessionId = params.get('session_id');
      
      // If not in query params, check hash
      if (!sessionId && location.hash) {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        sessionId = hashParams.get('session_id');
      }
      
      if (sessionId && !processingOAuth) {
        setProcessingOAuth(true);
        setLoading(true);
        setError('');
        console.log('Found session_id, processing OAuth callback...');
        
        try {
          await processSession(sessionId);
          console.log('OAuth session processed successfully');
          // Use window.location for guaranteed redirect on iOS Safari
          window.location.href = '/';
        } catch (err) {
          console.error('OAuth callback error:', err);
          setError('Erro ao processar login com Google. Tente novamente.');
          window.history.replaceState({}, document.title, '/login');
          setLoading(false);
          setProcessingOAuth(false);
        }
      }
    };

    processOAuthCallback();
  }, [location, processSession, processingOAuth]);

  // Redirect if already authenticated (but not during auth in progress)
  useEffect(() => {
    if (isAuthenticated && !authLoading && !loading && !authInProgress) {
      console.log('User is authenticated, redirecting to home...');
      window.location.href = '/';
    }
  }, [isAuthenticated, authLoading, loading, authInProgress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        console.log('Attempting login...');
        const result = await loginWithEmail(email, password);
        console.log('Login successful:', result);
        setSuccess('Login realizado com sucesso! Redirecionando...');
        
        // Use window.location for guaranteed redirect on all browsers including iOS Safari
        // This forces a full page load which ensures state is properly initialized
        window.location.href = '/';
      } else {
        console.log('Attempting registration...');
        const result = await registerWithEmail(email, password, name);
        console.log('Registration successful:', result);
        setSuccess('Cadastro realizado com sucesso! Redirecionando...');
        
        // Use window.location for guaranteed redirect on all browsers including iOS Safari
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Auth error:', err);
      const errorMessage = err.response?.data?.detail || 'Erro ao processar. Tente novamente.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    window.location.href = `${API_URL}/api/auth/google`;
  };

  const features = [
    { icon: BookOpen, title: '100 Lições', desc: 'Conteúdo prático para o dia a dia' },
    { icon: Zap, title: 'Microlearning', desc: 'Lições de 5-10 minutos' },
    { icon: Trophy, title: 'Gamificação', desc: 'Ganhe XP e conquistas' },
    { icon: Globe, title: 'Para Brasileiros', desc: 'Focado em quem vive no Japão' },
  ];

  const benefits = [
    'Inglês para situações reais de trabalho',
    'Vocabulário para fábricas e logística',
    'Frases prontas para usar no dia a dia',
    'AI Tutor para praticar conversação',
  ];

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">FO</span>
            </div>
            <span className="font-display font-bold text-xl">Fuse Online BR</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Recursos
            </a>
            <a href="#benefits" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Benefícios
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Side - Hero Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  Curso de Inglês para Brasileiros
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight">
                  Aprenda Inglês de forma
                  <span className="text-primary"> inteligente</span>
                </h1>
                
                <p className="text-lg text-muted-foreground max-w-lg">
                  Curso especialmente desenvolvido para brasileiros que vivem no Japão. 
                  Inglês prático para trabalho, dia a dia e situações reais.
                </p>
              </div>

              {/* Features Grid */}
              <div id="features" className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors"
                  >
                    <feature.icon className="w-8 h-8 text-primary mb-2" />
                    <h3 className="font-semibold">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                ))}
              </div>

              {/* Benefits */}
              <div id="benefits" className="space-y-3">
                <h3 className="font-semibold text-lg">O que você vai aprender:</h3>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Login/Register Form */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              <div className="card-elevated p-6 sm:p-8 rounded-2xl">
                {/* Tab Switcher */}
                <div className="flex rounded-xl bg-muted p-1 mb-6">
                  <button
                    onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
                    className={cn(
                      'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                      isLogin 
                        ? 'bg-background shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    Entrar
                  </button>
                  <button
                    onClick={() => { setIsLogin(false); setError(''); setSuccess(''); }}
                    className={cn(
                      'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                      !isLogin 
                        ? 'bg-background shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    Cadastrar
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Nome completo</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Seu nome"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="pl-10"
                          required={!isLogin}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Senha</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {!isLogin && (
                      <p className="text-xs text-muted-foreground">Mínimo de 6 caracteres</p>
                    )}
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="p-3 rounded-lg bg-success/10 text-success text-sm flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {success}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full btn-primary-gradient"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Processando...
                      </>
                    ) : (
                      <>
                        {isLogin ? 'Entrar' : 'Criar conta'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">ou continue com</span>
                  </div>
                </div>

                {/* Social Login Buttons */}
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    )}
                    Continuar com Google
                  </Button>
                </div>

                {/* Terms */}
                <p className="text-xs text-center text-muted-foreground mt-6">
                  Ao continuar, você concorda com nossos{' '}
                  <a href="#" className="text-primary hover:underline">Termos de Uso</a>
                  {' '}e{' '}
                  <a href="#" className="text-primary hover:underline">Política de Privacidade</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">FO</span>
            </div>
            <span className="font-display font-semibold">Fuse Online BR</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 Fuse Online BR. Todos os direitos reservados.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            fuseschool.online
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
